import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  COMMON_RATIOS,
  SOCIAL_PRESETS,
  PRINT_SIZES,
  type AspectRatio,
} from "@/data/aspectRatios";
import {
  solveDimension,
  findRatio,
} from "@/lib/calculators/aspectRatio";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type Tab = "solve" | "find" | "common";

const T = {
  h1: { en: "Aspect Ratio Calculator", bn: "অ্যাসপেক্ট রেশিও ক্যালকুলেটর" },
  desc: {
    en: "Solve missing width or height for any aspect ratio, simplify dimensions to a clean ratio, and pick from common presets for video, photo, web and social media.",
    bn: "যেকোনো অ্যাসপেক্ট রেশিওর জন্য অজানা প্রস্থ বা উচ্চতা বের করুন, ডাইমেনশন সরল করুন এবং ভিডিও, ফটো, ওয়েব ও সোশ্যাল মিডিয়ার প্রিসেট থেকে বেছে নিন।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  tSolve:  { en: "Solve dimension", bn: "ডাইমেনশন বের করুন" },
  tFind:   { en: "Find ratio",      bn: "রেশিও বের করুন" },
  tCommon: { en: "Common ratios",   bn: "প্রচলিত রেশিও" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "What is aspect ratio?", bn: "অ্যাসপেক্ট রেশিও কী?" },
    a: { en: "Aspect ratio is the proportional relationship between an image's width and height, written as W:H. A 16:9 image is 16 units wide for every 9 units tall — so 1920×1080, 1280×720 and 3840×2160 all have the same shape.",
         bn: "অ্যাসপেক্ট রেশিও হলো ছবির প্রস্থ ও উচ্চতার অনুপাত, লেখা হয় W:H আকারে। ১৬:৯ মানে প্রতি ৯ ইউনিট উচ্চতার জন্য ১৬ ইউনিট প্রস্থ। ১৯২০×১০৮০, ১২৮০×৭২০ এবং ৩৮৪০×২১৬০ — সবগুলোর আকার একই।" } },
  { q: { en: "What is 16:9 in pixels?", bn: "১৬:৯ পিক্সেলে কত?" },
    a: { en: "Common 16:9 sizes: 1280×720 (HD), 1920×1080 (Full HD), 2560×1440 (QHD), 3840×2160 (4K UHD), 7680×4320 (8K UHD).",
         bn: "১৬:৯ এর প্রচলিত মাপ: ১২৮০×৭২০ (এইচডি), ১৯২০×১০৮০ (ফুল এইচডি), ২৫৬০×১৪৪০ (কিউএইচডি), ৩৮৪০×২১৬০ (৪কে), ৭৬৮০×৪৩২০ (৮কে)।" } },
  { q: { en: "Best aspect ratio for YouTube?", bn: "ইউটিউবের জন্য সেরা অ্যাসপেক্ট রেশিও?" },
    a: { en: "Standard YouTube videos: 16:9 (1920×1080 or 3840×2160). Shorts: 9:16 (1080×1920). Thumbnails: 16:9 at 1280×720.",
         bn: "স্ট্যান্ডার্ড ইউটিউব ভিডিও: ১৬:৯ (১৯২০×১০৮০ বা ৩৮৪০×২১৬০)। শর্টস: ৯:১৬ (১০৮০×১৯২০)। থাম্বনেইল: ১৬:৯ (১২৮০×৭২০)।" } },
  { q: { en: "ফেসবুক ভিডিওর জন্য সেরা অ্যাসপেক্ট রেশিও কোনটি?", bn: "ফেসবুক ভিডিওর জন্য সেরা অ্যাসপেক্ট রেশিও কোনটি?" },
    a: { en: "ফিড ভিডিও: ৪:৫ (১০৮০×১৩৫০) মোবাইলে সবচেয়ে বেশি জায়গা নেয়। স্টোরি/রিল: ৯:১৬ (১০৮০×১৯২০)। ল্যান্ডস্কেপ ভিডিও: ১৬:৯।",
         bn: "ফিড ভিডিও: ৪:৫ (১০৮০×১৩৫০) মোবাইলে সবচেয়ে বেশি জায়গা নেয়। স্টোরি/রিল: ৯:১৬ (১০৮০×১৯২০)। ল্যান্ডস্কেপ: ১৬:৯।" } },
  { q: { en: "Why does my photo look cropped when printed?", bn: "প্রিন্টে ছবি কাটা দেখায় কেন?" },
    a: { en: "Camera ratios (3:2 from DSLRs, 4:3 from phones) often don't match print sizes (4×6 = 3:2, 5×7 = 7:5, 8×10 = 5:4). The lab crops to fit. Solve: shoot in the right ratio or leave headroom.",
         bn: "ক্যামেরার রেশিও (DSLR ৩:২, ফোন ৪:৩) প্রিন্টের সাইজের (৫×৭, ৮×১০) সঙ্গে মেলে না, ফলে ল্যাব ক্রপ করে। সমাধান: ঠিক রেশিওতে তুলুন বা পাশে জায়গা রাখুন।" } },
  { q: { en: "What's letterboxing?", bn: "লেটারবক্সিং কী?" },
    a: { en: "Black bars at top and bottom that appear when a wider video (e.g., 21:9) is shown on a narrower screen (16:9) without cropping. Pillarboxing is the opposite — black bars on the sides for vertical content on a horizontal screen.",
         bn: "যখন বেশি প্রশস্ত ভিডিও (যেমন ২১:৯) সরু স্ক্রিনে (১৬:৯) ক্রপ ছাড়া দেখানো হয়, উপরে-নিচে কালো বার আসে — এটি লেটারবক্সিং। উল্টোটা পিলারবক্সিং (পাশে কালো বার)।" } },
];

export const Route = createFileRoute("/aspect-ratio-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Aspect Ratio Calculator — 16:9, 4:3 Image & Video",
      description:
        "Free aspect ratio calculator. Calculate width, height, or ratio for video, photo, screen. 16:9, 4:3, 9:16 presets for YouTube, Instagram, TikTok. অ্যাসপেক্ট রেশিও ক্যালকুলেটর।",
      path: "/aspect-ratio-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Aspect Ratio", path: "/aspect-ratio-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: AspectRatioPage,
});

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function AspectRatioPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 2) => {
    if (!Number.isFinite(n)) return "—";
    const s = (Math.round(n * 10 ** d) / 10 ** d).toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [tab, setTab] = useState<Tab>("solve");

  // Shared state for tab 1 (solve) — also written to by tab 3 (common picker)
  const [rW, setRW] = useState("16");
  const [rH, setRH] = useState("9");
  const [w, setW] = useState("1920");
  const [h, setH] = useState("");
  const [unit, setUnit] = useState<"px" | "in" | "cm" | "mm">("px");
  const [lastTouched, setLastTouched] = useState<"w" | "h">("w");

  const applyRatio = (rw: number, rh: number) => {
    setRW(String(rw));
    setRH(String(rh));
    setTab("solve");
  };

  return (
    <CalculatorLayout
      slug="aspect-ratio-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Maximize"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-xl border border-border bg-card p-1">
        {([["solve", tr("tSolve")], ["find", tr("tFind")], ["common", tr("tCommon")]] as [Tab, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors",
              tab === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "solve" && (
          <SolveTab
            lang={lang} useBn={useBn} num={num} fmt={fmt}
            rW={rW} rH={rH} w={w} h={h} unit={unit}
            setRW={setRW} setRH={setRH} setW={setW} setH={setH} setUnit={setUnit}
            lastTouched={lastTouched} setLastTouched={setLastTouched}
          />
        )}
        {tab === "find" && <FindTab lang={lang} useBn={useBn} num={num} fmt={fmt} />}
        {tab === "common" && <CommonTab lang={lang} onPick={applyRatio} />}
      </div>

      {/* Social presets — shown under solve tab */}
      {tab === "solve" && (
        <section className="mt-8 space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Social media quick presets" : "সোশ্যাল মিডিয়া দ্রুত প্রিসেট"}
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {SOCIAL_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  // derive ratio from w/h (use simplified if common, else raw)
                  const info = findRatio(p.w, p.h);
                  if (info) { setRW(String(info.w)); setRH(String(info.h)); }
                  setW(String(p.w));
                  setH(String(p.h));
                  setLastTouched("w");
                }}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card p-3 text-left text-sm hover:border-accent"
              >
                <div className="min-w-0">
                  <p className="font-medium">{p.platform}</p>
                  <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{lang === "en" ? p.type.en : p.type.bn}</p>
                </div>
                <div className="text-right text-xs text-muted-foreground tabular-nums">
                  <div>{num(p.w)}×{num(p.h)}</div>
                  <div>{p.ratio}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Print sizes */}
      <section className="mt-8 space-y-3">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Photo print sizes" : "ফটো প্রিন্ট সাইজ"}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">{lang === "en" ? "Size (inches)" : "মাপ (ইঞ্চি)"}</th>
                <th className="p-2 text-left">{lang === "en" ? "Ratio" : "রেশিও"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Decimal" : "দশমিক"}</th>
                <th className="p-2 text-left">{lang === "en" ? "Use" : "ব্যবহার"}</th>
              </tr>
            </thead>
            <tbody>
              {PRINT_SIZES.map((p) => (
                <tr key={p.inches} className="border-t border-border">
                  <td className="p-2 font-medium">{p.inches}</td>
                  <td className="p-2">{p.ratio}</td>
                  <td className="p-2 text-right tabular-nums">{fmt(p.decimal, 4)}</td>
                  <td className={cn("p-2 text-muted-foreground", lang === "bn" && "font-bn")}>
                    {lang === "en" ? p.use.en : p.use.bn}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Letterboxing & pillarboxing" : "লেটারবক্সিং ও পিলারবক্সিং"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "When a video's aspect ratio doesn't match the screen, the player can either crop or add black bars. Bars on top and bottom = letterboxing (wider video on narrower screen, e.g., 21:9 movie on a 16:9 TV). Bars on the sides = pillarboxing (narrower video on wider screen, e.g., a 4:3 clip on a 16:9 monitor)."
              : "ভিডিওর রেশিও স্ক্রিনের সঙ্গে না মিললে প্লেয়ার ক্রপ করে বা কালো বার যোগ করে। উপরে-নিচে বার = লেটারবক্সিং (২১:৯ মুভি ১৬:৯ টিভিতে)। পাশে বার = পিলারবক্সিং (৪:৩ ক্লিপ ১৬:৯ মনিটরে)।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "How to find a ratio (step by step)" : "রেশিও বের করার নিয়ম"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Compute the GCD (greatest common divisor) of width and height." : "প্রস্থ ও উচ্চতার গসাগু (GCD) বের করুন।"}</li>
            <li>{lang === "en" ? "Divide each by the GCD: e.g., 1920÷120=16, 1080÷120=9 → 16:9." : "প্রত্যেকটিকে GCD দিয়ে ভাগ করুন: ১৯২০÷১২০=১৬, ১০৮০÷১২০=৯ → ১৬:৯।"}</li>
            <li>{lang === "en" ? "If the result is awkward (e.g., 427:240), check the decimal — 1.778 ≈ 16:9." : "অস্বাভাবিক ফল হলে দশমিক দেখুন — ১.৭৭৮ ≈ ১৬:৯।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/square-feet-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Square feet" : "বর্গফুট"}</Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Percentage" : "শতকরা"}</Link>
            <Link to="/feet-to-meter" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Feet ↔ Meter" : "ফুট ↔ মিটার"}</Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

/* -------------------- Tab 1: Solve -------------------- */

function SolveTab(props: {
  lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string;
  rW: string; rH: string; w: string; h: string; unit: "px" | "in" | "cm" | "mm";
  setRW: (s: string) => void; setRH: (s: string) => void; setW: (s: string) => void; setH: (s: string) => void;
  setUnit: (u: "px" | "in" | "cm" | "mm") => void;
  lastTouched: "w" | "h"; setLastTouched: (s: "w" | "h") => void;
}) {
  const { lang, useBn, num, fmt, rW, rH, w, h, unit, setRW, setRH, setW, setH, setUnit, lastTouched, setLastTouched } = props;

  const ratioW = parseFloat(rW) || 0;
  const ratioH = parseFloat(rH) || 0;
  const widthN = parseFloat(w);
  const heightN = parseFloat(h);

  // auto-derive the dimension that wasn't last touched
  const solved = useMemo(() => {
    if (!ratioW || !ratioH) return null;
    if (lastTouched === "w" && Number.isFinite(widthN) && widthN > 0) {
      return solveDimension({ ratioW, ratioH, width: widthN });
    }
    if (lastTouched === "h" && Number.isFinite(heightN) && heightN > 0) {
      return solveDimension({ ratioW, ratioH, height: heightN });
    }
    if (Number.isFinite(widthN) && widthN > 0) return solveDimension({ ratioW, ratioH, width: widthN });
    if (Number.isFinite(heightN) && heightN > 0) return solveDimension({ ratioW, ratioH, height: heightN });
    return null;
  }, [ratioW, ratioH, widthN, heightN, lastTouched]);

  const finalW = solved?.width ?? (Number.isFinite(widthN) ? widthN : 0);
  const finalH = solved?.height ?? (Number.isFinite(heightN) ? heightN : 0);
  const totalArea = finalW * finalH;

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${Math.round(finalW)}×${Math.round(finalH)} (${ratioW}:${ratioH})`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Aspect ratio" : "অ্যাসপেক্ট রেশিও"}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <NumInput value={rW} onChange={setRW} useBn={useBn} />
            <span className="text-2xl font-semibold text-muted-foreground">:</span>
            <NumInput value={rH} onChange={setRH} useBn={useBn} />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <p className={cn("text-xs font-semibold uppercase text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Dimensions" : "ডাইমেনশন"}
            </p>
            <div className="inline-flex rounded-full border border-border bg-background p-0.5">
              {(["px", "in", "cm", "mm"] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUnit(u)}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium",
                    unit === u ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] text-muted-foreground">{lang === "en" ? "Width" : "প্রস্থ"}</label>
              <NumInput
                value={w}
                onChange={(v) => { setW(v); setLastTouched("w"); }}
                useBn={useBn}
                placeholder={lastTouched === "h" && solved ? num(Math.round(solved.width * 100) / 100) : ""}
              />
            </div>
            <div>
              <label className="block text-[11px] text-muted-foreground">{lang === "en" ? "Height" : "উচ্চতা"}</label>
              <NumInput
                value={h}
                onChange={(v) => { setH(v); setLastTouched("h"); }}
                useBn={useBn}
                placeholder={lastTouched === "w" && solved ? num(Math.round(solved.height * 100) / 100) : ""}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Result + preview */}
      {solved && (
        <motion.div
          key={`${ratioW}-${ratioH}-${finalW.toFixed(2)}-${finalH.toFixed(2)}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid gap-4 rounded-2xl border border-accent/40 bg-accent/5 p-6 md:grid-cols-[1fr_auto]"
        >
          <div>
            <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Result" : "ফলাফল"}
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">
              {fmt(finalW, 2)} × {fmt(finalH, 2)} <span className="text-base font-normal text-muted-foreground">{unit}</span>
            </p>
            <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Ratio " : "রেশিও "}{num(ratioW)}:{num(ratioH)} · {lang === "en" ? "Decimal " : "দশমিক "}{fmt(ratioW / ratioH, 4)}
              {" · "}
              {lang === "en" ? "Area " : "ক্ষেত্রফল "}{fmt(totalArea, 0)} {unit}²
            </p>
            <button
              type="button"
              onClick={copy}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:border-accent"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? (lang === "en" ? "Copied" : "কপি হয়েছে") : (lang === "en" ? "Copy" : "কপি")}
            </button>
          </div>
          <RatioPreview w={ratioW} h={ratioH} label={`${ratioW}:${ratioH}`} />
        </motion.div>
      )}
    </div>
  );
}

function NumInput({ value, onChange, useBn, placeholder }: { value: string; onChange: (v: string) => void; useBn: boolean; placeholder?: string }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={useBn ? toBengaliNumerals(value) : value}
      onChange={(e) => onChange(stripBn(e.target.value))}
      placeholder={placeholder}
      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base font-semibold tabular-nums focus:border-accent focus:outline-none"
    />
  );
}

/* -------------------- Tab 2: Find ratio -------------------- */

function FindTab({ lang, useBn, num, fmt }: { lang: Lang; useBn: boolean; num: (n: number | string) => string; fmt: (n: number, d?: number) => string }) {
  const [w, setW] = useState("1920");
  const [h, setH] = useState("1080");
  const wn = parseFloat(w);
  const hn = parseFloat(h);
  const info = useMemo(() => findRatio(wn, hn), [wn, hn]);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="block text-xs font-medium text-muted-foreground">{lang === "en" ? "Width" : "প্রস্থ"}</label>
          <NumInput value={w} onChange={setW} useBn={useBn} />
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="block text-xs font-medium text-muted-foreground">{lang === "en" ? "Height" : "উচ্চতা"}</label>
          <NumInput value={h} onChange={setH} useBn={useBn} />
        </div>
      </div>

      {info && (
        <motion.div
          key={`${w}-${h}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid gap-4 rounded-2xl border border-accent/40 bg-accent/5 p-6 md:grid-cols-[1fr_auto]"
        >
          <div>
            <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
              {lang === "en" ? "Aspect Ratio" : "অ্যাসপেক্ট রেশিও"}
            </p>
            <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">{info.label}</p>
            <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {num(Math.round(wn))} × {num(Math.round(hn))} · {lang === "en" ? "Decimal " : "দশমিক "}{fmt(info.decimal, 4)}
              {!info.matchExact && info.match && (lang === "en" ? " (≈ closest common)" : " (≈ নিকটতম প্রচলিত)")}
            </p>
            {(info.commonName || info.resolutionName) && (
              <p className={cn("mt-2 text-sm", lang === "bn" && "font-bn")}>
                {info.resolutionName && <span className="mr-2 rounded-md bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">{info.resolutionName}</span>}
                {info.commonName && (
                  <span className="text-muted-foreground">
                    {lang === "en" ? info.commonName.en : info.commonName.bn}
                  </span>
                )}
              </p>
            )}
          </div>
          <RatioPreview w={info.w} h={info.h} label={info.label} />
        </motion.div>
      )}
    </div>
  );
}

/* -------------------- Tab 3: Common -------------------- */

function CommonTab({ lang, onPick }: { lang: Lang; onPick: (w: number, h: number) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {COMMON_RATIOS.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onPick(r.w, r.h)}
          className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-4 text-left hover:border-accent"
        >
          <div className="flex w-full items-center justify-between">
            <div>
              <p className="text-lg font-bold">{r.label}</p>
              <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {lang === "en" ? r.name.en : r.name.bn}
              </p>
            </div>
            <RatioPreview w={r.w} h={r.h} label="" small />
          </div>
          <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en" ? r.uses.en : r.uses.bn}
          </p>
        </button>
      ))}
    </div>
  );
}

/* -------------------- Visual preview -------------------- */

function RatioPreview({ w, h, label, small = false }: { w: number; h: number; label: string; small?: boolean }) {
  if (!w || !h) return null;
  const max = small ? 64 : 140;
  const ratio = w / h;
  const boxW = ratio >= 1 ? max : max * ratio;
  const boxH = ratio >= 1 ? max / ratio : max;
  return (
    <div className="flex items-center justify-center" style={{ width: max, height: max }}>
      <div
        style={{ width: boxW, height: boxH }}
        className="flex items-center justify-center rounded-md border-2 border-accent/60 bg-accent/10 text-[10px] font-medium text-accent"
      >
        {label}
      </div>
    </div>
  );
}

export type { AspectRatio };
